/**
 * useCrapsAudio — procedural sound effects + ambient background music for the
 * Craps table. Uses the Web Audio API so there are no external audio assets:
 * every sound is synthesized on the fly from oscillators + filtered noise.
 *
 * Returns { play, setEffects, setMusic, effectsOn, musicOn }.
 *   play(name) — trigger a one-shot SFX
 *   setEffects(bool)  — toggle sound effects
 *   setMusic(bool)    — toggle background ambient music
 *
 * Named effects:
 *   chip        · placing a bet
 *   roll        · dice tumbling (starts and holds until you call `roll_end`)
 *   roll_end    · dice thud / stop
 *   win_small   · small win chime
 *   win_big     · big win fanfare (point hit)
 *   lose        · small loss buzzer
 *   seven_out   · sad trombone
 *   yo          · yo eleven — quick "yo!"
 *   snake_eyes  · snake eyes low blip
 *   clack       · roulette ball fret knock
 *   wheel_whoosh · low-frequency roulette wheel airflow (~5s, decays)
 *   coin_cascade · cascade of coin tings — big-win celebration overlay
 *   vault_kaching · brassy fanfare + vault-door boom — monster-win (>=$500)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const LS_EFFECTS = 'craps-fx-on';
const LS_MUSIC = 'craps-music-on';

export function useCrapsAudio() {
    const ctxRef = useRef(null);
    const masterRef = useRef(null);   // master gain for SFX
    const musicGainRef = useRef(null); // separate gain for ambient loop
    const musicNodesRef = useRef([]);  // active music oscillator nodes for teardown
    const sampleCacheRef = useRef({});  // decoded AudioBuffers for uploaded sound files
    const [effectsOn, setEffectsOnState] = useState(() => localStorage.getItem(LS_EFFECTS) !== '0');
    const [musicOn, setMusicOnState] = useState(() => localStorage.getItem(LS_MUSIC) === '1');

    // Lazy-init the audio graph the first time it's needed.
    const ensure = useCallback(() => {
        if (ctxRef.current) return ctxRef.current;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        const ctx = new Ctx();
        const master = ctx.createGain();
        master.gain.value = 0.28;
        master.connect(ctx.destination);
        const musicGain = ctx.createGain();
        musicGain.gain.value = 0;   // stays 0 until user turns music on
        musicGain.connect(ctx.destination);
        ctxRef.current = ctx;
        masterRef.current = master;
        musicGainRef.current = musicGain;
        return ctx;
    }, []);

    const setEffects = useCallback((on) => {
        setEffectsOnState(on);
        localStorage.setItem(LS_EFFECTS, on ? '1' : '0');
    }, []);

    // ── Ambient background music: two low sustained pads + a slow arpeggio ──
    const startMusic = useCallback(() => {
        const ctx = ensure();
        if (!ctx || !musicGainRef.current) return;
        // Resume if suspended (browsers require gesture)
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        // Fade the music gain in over 1.5s
        const g = musicGainRef.current;
        g.gain.cancelScheduledValues(ctx.currentTime);
        g.gain.setValueAtTime(g.gain.value, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 1.5);

        // Two slow drone oscillators for a warm pad
        const drone1 = ctx.createOscillator();
        drone1.type = 'sine'; drone1.frequency.value = 110; // A2
        const drone2 = ctx.createOscillator();
        drone2.type = 'sine'; drone2.frequency.value = 165; // E3 (fifth)
        const droneGain = ctx.createGain();
        droneGain.gain.value = 0.5;
        drone1.connect(droneGain);
        drone2.connect(droneGain);
        droneGain.connect(g);
        drone1.start();
        drone2.start();

        // Slow arpeggio: A minor pentatonic notes above the drone
        const notes = [330, 392, 440, 523, 587]; // E4 G4 A4 C5 D5
        const arpGain = ctx.createGain();
        arpGain.gain.value = 0.25;
        arpGain.connect(g);
        let i = 0;
        const arpTick = setInterval(() => {
            if (!ctxRef.current || !musicGainRef.current) return;
            const t = ctx.currentTime;
            const o = ctx.createOscillator();
            o.type = 'triangle';
            o.frequency.value = notes[i % notes.length];
            const eg = ctx.createGain();
            eg.gain.setValueAtTime(0, t);
            eg.gain.linearRampToValueAtTime(0.5, t + 0.05);
            eg.gain.exponentialRampToValueAtTime(0.001, t + 1.8);
            o.connect(eg); eg.connect(arpGain);
            o.start(t);
            o.stop(t + 1.9);
            i++;
        }, 1600);

        musicNodesRef.current = [drone1, drone2, droneGain, arpGain, arpTick];
    }, [ensure]);

    const stopMusic = useCallback(() => {
        const ctx = ctxRef.current;
        if (!ctx || !musicGainRef.current) return;
        // Fade out
        const g = musicGainRef.current;
        g.gain.cancelScheduledValues(ctx.currentTime);
        g.gain.setValueAtTime(g.gain.value, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.8);
        setTimeout(() => {
            musicNodesRef.current.forEach((n) => {
                try {
                    if (typeof n === 'number') clearInterval(n);
                    else if (n.stop) n.stop();
                    else if (n.disconnect) n.disconnect();
                } catch { /* ignore */ }
            });
            musicNodesRef.current = [];
        }, 900);
    }, []);

    const setMusic = useCallback((on) => {
        setMusicOnState(on);
        localStorage.setItem(LS_MUSIC, on ? '1' : '0');
        if (on) startMusic();
        else stopMusic();
    }, [startMusic, stopMusic]);

    // Clean up on unmount
    useEffect(() => () => {
        try {
            stopMusic();
            if (ctxRef.current) ctxRef.current.close();
        } catch { /* ignore */ }
    }, [stopMusic]);

    // ── Sound effects — every SFX gets its own tiny function ──
    const beep = useCallback((freq, duration, type = 'sine', vol = 0.4) => {
        const ctx = ensure();
        if (!ctx || !masterRef.current) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const t = ctx.currentTime;
        const o = ctx.createOscillator();
        o.type = type; o.frequency.value = freq;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(vol, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + duration);
        o.connect(g); g.connect(masterRef.current);
        o.start(t); o.stop(t + duration + 0.05);
    }, [ensure]);

    const noise = useCallback((duration, filterFreq = 1500, vol = 0.25) => {
        const ctx = ensure();
        if (!ctx || !masterRef.current) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass'; filter.frequency.value = filterFreq; filter.Q.value = 1.2;
        const g = ctx.createGain();
        const t = ctx.currentTime;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(vol, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, t + duration);
        src.connect(filter); filter.connect(g); g.connect(masterRef.current);
        src.start(t); src.stop(t + duration);
    }, [ensure]);

    const play = useCallback((name) => {
        if (!effectsOn) return;
        switch (name) {
            case 'chip':
                // Short high-pitched tap — clay chip on felt
                beep(2200, 0.06, 'square', 0.18);
                setTimeout(() => beep(1800, 0.05, 'triangle', 0.12), 20);
                break;
            case 'roll':
                // Dice tumbling — 0.9s of filtered noise sweeping through mids
                noise(0.9, 900, 0.28);
                break;
            case 'roll_end':
                // Two soft thuds — dice hitting the back wall then settling
                noise(0.08, 300, 0.35);
                setTimeout(() => noise(0.06, 220, 0.30), 90);
                break;
            case 'card_flip':
                // Crisp card snap/slap onto the felt — bright swipe + a short
                // low body thump so it's clearly audible on every dealt card.
                noise(0.07, 5200, 0.5);
                setTimeout(() => noise(0.05, 760, 0.34), 12);
                break;
            case 'card_deal':
                // Soft card sliding onto felt — mid-frequency short whoosh
                noise(0.14, 1600, 0.20);
                break;
            case 'win_small':
                // Rising two-tone chime
                beep(659, 0.15, 'triangle', 0.35);  // E5
                setTimeout(() => beep(880, 0.25, 'triangle', 0.35), 120); // A5
                break;
            case 'win_big':
                // Full fanfare — point hit!
                [523, 659, 784, 1047].forEach((f, i) => {  // C5-E5-G5-C6 arpeggio
                    setTimeout(() => beep(f, 0.35, 'triangle', 0.42), i * 90);
                });
                setTimeout(() => beep(1319, 0.6, 'triangle', 0.35), 380); // final E6
                break;
            case 'lose':
                // Short descending buzz
                beep(220, 0.18, 'sawtooth', 0.25);
                setTimeout(() => beep(165, 0.22, 'sawtooth', 0.22), 100);
                break;
            case 'seven_out':
                // Sad trombone descent
                [440, 392, 349, 293].forEach((f, i) => {
                    setTimeout(() => beep(f, 0.35, 'sawtooth', 0.32), i * 140);
                });
                break;
            case 'yo':
                // Quick uplifting yo-11 blip
                beep(1200, 0.08, 'square', 0.28);
                setTimeout(() => beep(1800, 0.12, 'triangle', 0.32), 70);
                break;
            case 'snake_eyes':
                // Low ominous blip
                beep(140, 0.4, 'sawtooth', 0.28);
                break;
            case 'pair_alert':
                // Two-tone chip clink — draws the eye to the PAIR badge on the
                // felt without being intrusive. Bright, short, sharp — clay
                // chip stacking onto another clay chip.
                beep(1800, 0.06, 'triangle', 0.28);
                setTimeout(() => beep(2400, 0.08, 'triangle', 0.24), 55);
                break;
            case 'clack':
                // Sharp wooden knock — roulette ball hitting a fret
                beep(1400, 0.04, 'square', 0.22);
                setTimeout(() => beep(900, 0.05, 'triangle', 0.16), 12);
                break;
            case 'wheel_whoosh': {
                // Soft low-frequency airflow — the sound of a spinning roulette
                // wheel. Filtered noise sweeps from ~380Hz down to ~90Hz over
                // ~5s and decays in amplitude, mirroring the deceleration of
                // the wheel. Uses a lowpass filter for a warm, dark tone.
                const ctx = ensure();
                if (!ctx || !masterRef.current) break;
                if (ctx.state === 'suspended') ctx.resume().catch(() => {});
                const duration = 5.0;
                const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
                const data = buffer.getChannelData(0);
                for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
                const src = ctx.createBufferSource();
                src.buffer = buffer;
                const filter = ctx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.Q.value = 8;
                const t0 = ctx.currentTime;
                // Sweep the filter frequency down as the wheel decelerates
                filter.frequency.setValueAtTime(380, t0);
                filter.frequency.exponentialRampToValueAtTime(90, t0 + duration);
                // Amplitude envelope: rise quickly, hold, then decay
                const g = ctx.createGain();
                g.gain.setValueAtTime(0, t0);
                g.gain.linearRampToValueAtTime(0.18, t0 + 0.35);
                g.gain.linearRampToValueAtTime(0.14, t0 + 3.2);
                g.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
                // Subtle sub-oscillator hum for body
                const sub = ctx.createOscillator();
                sub.type = 'sine';
                sub.frequency.setValueAtTime(65, t0);
                sub.frequency.exponentialRampToValueAtTime(38, t0 + duration);
                const subGain = ctx.createGain();
                subGain.gain.setValueAtTime(0, t0);
                subGain.gain.linearRampToValueAtTime(0.06, t0 + 0.4);
                subGain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
                src.connect(filter); filter.connect(g); g.connect(masterRef.current);
                sub.connect(subGain); subGain.connect(masterRef.current);
                src.start(t0); src.stop(t0 + duration);
                sub.start(t0); sub.stop(t0 + duration);
                break;
            }
            case 'coin_cascade': {
                // Cascade of coins hitting the tray — a shower of quick
                // metallic "ting" tones at randomized pitches (~1.6-2.6kHz)
                // with random timing over ~1.4s. Each ting is a short
                // triangle-wave with a sharp attack and quick decay so it
                // reads as "coins" not "chimes".
                const ctx = ensure();
                if (!ctx || !masterRef.current) break;
                if (ctx.state === 'suspended') ctx.resume().catch(() => {});
                const t0 = ctx.currentTime;
                const count = 22;
                for (let i = 0; i < count; i++) {
                    const delay = i * 0.045 + Math.random() * 0.06;
                    const freq = 1600 + Math.random() * 1000; // 1.6-2.6kHz
                    const vol = 0.14 + Math.random() * 0.10;
                    const dur = 0.09 + Math.random() * 0.06;
                    const t = t0 + delay;
                    const o = ctx.createOscillator();
                    o.type = 'triangle';
                    o.frequency.value = freq;
                    const g = ctx.createGain();
                    g.gain.setValueAtTime(0, t);
                    g.gain.linearRampToValueAtTime(vol, t + 0.005);
                    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
                    // Second harmonic overlay for metallic sheen
                    const o2 = ctx.createOscillator();
                    o2.type = 'sine';
                    o2.frequency.value = freq * 1.5;
                    const g2 = ctx.createGain();
                    g2.gain.setValueAtTime(0, t);
                    g2.gain.linearRampToValueAtTime(vol * 0.4, t + 0.005);
                    g2.gain.exponentialRampToValueAtTime(0.001, t + dur);
                    o.connect(g); g.connect(masterRef.current);
                    o2.connect(g2); g2.connect(masterRef.current);
                    o.start(t); o.stop(t + dur + 0.02);
                    o2.start(t); o2.stop(t + dur + 0.02);
                }
                break;
            }
            case 'vault_kaching': {
                // Monster-win vault-door horn — a brassy fanfare stab followed
                // by a low sustained rumble. Uses three detuned saw oscillators
                // (fundamental + fifth + octave) with a lowpass filter for the
                // brass timbre, plus a sub-oscillator for the vault-door boom.
                const ctx = ensure();
                if (!ctx || !masterRef.current) break;
                if (ctx.state === 'suspended') ctx.resume().catch(() => {});
                const t0 = ctx.currentTime;
                const duration = 1.6;
                // Brass fanfare — root C3, fifth G3, octave C4
                const notes = [130.81, 196.00, 261.63];
                const filter = ctx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(1800, t0);
                filter.frequency.exponentialRampToValueAtTime(600, t0 + duration);
                filter.Q.value = 4;
                const brassGain = ctx.createGain();
                brassGain.gain.setValueAtTime(0, t0);
                brassGain.gain.linearRampToValueAtTime(0.32, t0 + 0.06);
                brassGain.gain.linearRampToValueAtTime(0.25, t0 + 0.6);
                brassGain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
                notes.forEach((f, i) => {
                    const o = ctx.createOscillator();
                    o.type = 'sawtooth';
                    o.frequency.value = f;
                    // slight detune between voices for choir richness
                    o.detune.value = (i - 1) * 6;
                    o.connect(filter);
                    o.start(t0); o.stop(t0 + duration + 0.05);
                });
                filter.connect(brassGain); brassGain.connect(masterRef.current);
                // Sub boom — vault door slamming
                const sub = ctx.createOscillator();
                sub.type = 'sine';
                sub.frequency.setValueAtTime(55, t0);
                sub.frequency.exponentialRampToValueAtTime(32, t0 + duration);
                const subGain = ctx.createGain();
                subGain.gain.setValueAtTime(0, t0);
                subGain.gain.linearRampToValueAtTime(0.28, t0 + 0.04);
                subGain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
                sub.connect(subGain); subGain.connect(masterRef.current);
                sub.start(t0); sub.stop(t0 + duration + 0.05);
                // Bright metallic "cha-ching" burst on top — quick descending tones
                const chingNotes = [1568, 2093];  // G6, C7
                chingNotes.forEach((f, i) => {
                    const t = t0 + i * 0.09;
                    const o = ctx.createOscillator();
                    o.type = 'triangle';
                    o.frequency.value = f;
                    const g = ctx.createGain();
                    g.gain.setValueAtTime(0, t);
                    g.gain.linearRampToValueAtTime(0.24, t + 0.008);
                    g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
                    o.connect(g); g.connect(masterRef.current);
                    o.start(t); o.stop(t + 0.4);
                });
                break;
            }
            default:
                break;
        }
    }, [effectsOn, beep, noise, ensure]);

    // Play a real uploaded sound file through the SAME unlocked Web Audio context
    // used by every other SFX. Buffers are decoded once and cached. This avoids the
    // browser autoplay block that hits delayed `new Audio().play()` calls.
    const playSample = useCallback((url, vol = 0.9) => {
        if (!effectsOn) return;
        const ctx = ensure();
        if (!ctx || !masterRef.current) return;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const cache = sampleCacheRef.current;
        const fire = (buf) => {
            try {
                const src = ctx.createBufferSource();
                src.buffer = buf;
                const g = ctx.createGain();
                g.gain.value = vol;
                src.connect(g);
                g.connect(ctx.destination);   // own gain, independent of the 0.28 SFX master
                src.start();
            } catch { /* ignore */ }
        };
        if (cache[url]) { fire(cache[url]); return; }
        fetch(url)
            .then((r) => r.arrayBuffer())
            .then((ab) => new Promise((res, rej) => ctx.decodeAudioData(ab, res, rej)))
            .then((buf) => { cache[url] = buf; fire(buf); })
            .catch(() => {});
    }, [effectsOn, ensure]);

    return { play, playSample, setEffects, setMusic, effectsOn, musicOn };
}
